import { differenceInMilliseconds } from 'date-fns';
import api from 'libs/api';
import { IFilePath, TAttachment } from '.';

export type TFetchFiles = () => Promise<IFilePath[] | undefined>;

export const fetchFilesSilently = async <T>(
  request: Promise<{ data: T }>,
  pickFiles: (data: T) => IFilePath[] | undefined
): Promise<IFilePath[] | undefined> => {
  try {
    const { data } = await request;
    return pickFiles(data);
  } catch {
    return undefined;
  }
};

export const fetchEntityFiles = (entity: string, id: number) =>
  fetchFilesSilently(
    api.account.files.getAll({ entity, id }),
    (data: { resources: IFilePath[] }) => data.resources
  );

export const isFileUrlExpired = (file: IFilePath): boolean => {
  if (file.url_live_to) {
    return differenceInMilliseconds(new Date(file.url_live_to), new Date()) <= 0;
  } else {
    return false;
  }
};

export const getUrlPath = (file: IFilePath): string => file.url.split('?')[0];

const isFileUrlNewer = (fresh: IFilePath, file: IFilePath): boolean =>
  !file.url_live_to ||
  !fresh.url_live_to ||
  new Date(fresh.url_live_to).getTime() > new Date(file.url_live_to).getTime();

export const withReloadedFiles = <T extends TAttachment>(
  files: T[],
  reloaded: Map<string, IFilePath>
): T[] =>
  files.map((file) => {
    if (file instanceof File) return file;

    const fresh = reloaded.get(getUrlPath(file));
    return fresh && isFileUrlNewer(fresh, file) ? { ...file, ...fresh } : file;
  });
